"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Mail, Phone, User, Lock } from "lucide-react";

export default function OnlineOrderingLoyaltyScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* Left: copy */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
                {t("Customer ownership", "Dueño del cliente")}
              </div>
              <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal mb-6">
                {t("Your customer. ", "Tu cliente. ")}
                <br />
                <span className="text-redy-charcoal/30">{t("Your list.", "Tu lista.")}</span>
              </h2>
              <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mb-6">
                {t(
                  "Every direct order captures the customer's phone, email and name. They're yours. You can text the ones who opted in next Tuesday for the new menu. On a marketplace app, that relationship lives on the app.",
                  "Cada pedido directo captura el teléfono, email y nombre del cliente. Son tuyos. A los que aceptaron recibir mensajes les puedes mandar SMS el próximo martes con el menú nuevo. En una app de marketplace, esa relación vive en la app."
                )}
              </p>

              <div className="inline-flex items-center gap-2.5 bg-[#FAFAF8] px-4 py-3 rounded-full border border-black/5">
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(220, 38, 38, 0.12)" }}>
                  <Lock className="w-3.5 h-3.5" style={{ color: "#DC2626" }} />
                </div>
                <div className="text-[13px] font-bold text-redy-charcoal">
                  {t("Marketing texts go only to customers who opted in.", "Los SMS de marketing van solo a clientes que aceptaron.")}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: customer database mockup */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="relative bg-[#FAFAF8] rounded-[28px] border border-black/5 shadow-[0_20px_60px_rgba(0,0,0,0.04)] p-6 md:p-8"
            >
              {/* Header */}
              <div className="flex items-baseline justify-between mb-5">
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60">
                  {t("Your customer database", "Tu base de clientes")}
                </div>
                <div className="flex items-baseline gap-1.5">
                  <div className="text-[24px] font-black tabular-nums text-redy-charcoal">
                    1,247
                  </div>
                  <div className="text-[11px] font-bold" style={{ color: "#D7263D" }}>+18 {t("this week", "esta semana")}</div>
                </div>
              </div>

              {/* Customer cards */}
              <div className="space-y-2">
                {CUSTOMERS.map((c, i) => (
                  <motion.div
                    key={c.email}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.45, delay: 0.1 + 0.08 * i }}
                    className={`bg-white rounded-[16px] p-4 border border-black/5 flex items-center gap-4 ${
                      c.fresh ? "ring-2" : ""
                    }`}
                    style={c.fresh ? { boxShadow: "0 0 0 2px rgba(244,114,43,0.25)" } : undefined}
                  >
                    {/* Avatar */}
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-[13px] shrink-0"
                      style={{
                        background: c.fresh
                          ? "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)"
                          : `hsl(${c.hue}, 60%, 55%)`,
                      }}
                    >
                      {c.initial}
                    </div>

                    {/* Name + meta */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-[13px] font-black text-redy-charcoal truncate">
                          {c.name}
                        </div>
                        {c.fresh && (
                          <div
                            className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: "#F4722B" }}
                          >
                            {t("New", "Nuevo")}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-redy-secondary mt-0.5">
                        <span className="flex items-center gap-1">
                          <Mail className="w-2.5 h-2.5" />
                          <span className="truncate">{c.email}</span>
                        </span>
                        <span className="flex items-center gap-1 hidden sm:flex">
                          <Phone className="w-2.5 h-2.5" />
                          <span>{c.phone}</span>
                        </span>
                      </div>
                    </div>

                    {/* Orders count */}
                    <div className="text-right shrink-0">
                      <div className="text-[15px] font-black tabular-nums text-redy-charcoal">{c.orders}</div>
                      <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/60">
                        {t("orders", "pedidos")}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Footer action hint */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="mt-6 flex items-center justify-between bg-redy-charcoal rounded-[16px] px-4 py-3 text-white"
              >
                <div className="flex items-center gap-2">
                  <User className="w-3.5 h-3.5" style={{ color: "#F4722B" }} />
                  <span className="text-[12px] font-bold">
                    {t("Send a Tuesday SMS to your opted-in list", "Manda el SMS del martes a tu lista con opt-in")}
                  </span>
                </div>
                <div
                  className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full text-white"
                  style={{ backgroundColor: "#F4722B" }}
                >
                  {t("Ready", "Listo")}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

const CUSTOMERS = [
  { initial: "M", name: "María González", email: "maria.g@example.com", phone: "(214) 555-0142", orders: 12, hue: 200, fresh: false },
  { initial: "J", name: "James Carter", email: "jcarter@example.com", phone: "(214) 555-0207", orders: 8, hue: 30, fresh: false },
  { initial: "A", name: "Alex Rivera", email: "alex.rivera@example.com", phone: "(214) 555-0388", orders: 1, hue: 280, fresh: true },
  { initial: "D", name: "Daniela López", email: "dani.lopez@example.com", phone: "(214) 555-0476", orders: 5, hue: 340, fresh: false },
];
